import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, MessageSquareText, Loader2, Send } from "lucide-react";
import { API, BACKEND_URL } from "../lib/api";

/* DRFA Expert chat — streaming + precedent-aware.
 *
 * Why streaming: a non-streaming LLM call can take 30-90s on dense DRFA
 * questions and gets killed by the Cloudflare/ingress proxy at ~100s,
 * giving the assessor a "504 Gateway Timeout" with no answer. The SSE
 * endpoint flushes tokens as they arrive so the proxy never times out.
 *
 * Precedents: the backend retrieves matching assessor decisions from the
 * institutional-memory corpus, sends them in the first SSE event, and the
 * answer cites them as [Precedent N]. We render the precedents as inline
 * citation cards beneath the bot's reply so the assessor can audit which
 * past decisions influenced the answer.
 */
export function AIAssistant() {
  const sessionId = useMemo(() => {
    let s = localStorage.getItem("drfa_chat_session");
    if (!s) { s = "sess-" + Math.random().toString(36).slice(2,10); localStorage.setItem("drfa_chat_session", s); }
    return s;
  }, []);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scroller = useRef();
  useEffect(() => { axios.get(`${API}/ai/chat/${sessionId}`).then(r => setMsgs(r.data)); }, [sessionId]);
  useEffect(() => { scroller.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [msgs.length, sending]);

  const send = useCallback(async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setSending(true);
    setMsgs(m => [...m, { role: "user", content: text }]);

    // Reserve an assistant slot we'll mutate as tokens arrive.
    const assistantIdx = msgs.length + 1;
    setMsgs(m => [...m, { role: "assistant", content: "", precedents: [], streaming: true }]);

    try {
      const r = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!r.ok || !r.body) throw new Error("HTTP " + r.status);
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // SSE events are "data: <json>\n\n" — split on double newline.
        let nlIdx;
        while ((nlIdx = buf.indexOf("\n\n")) !== -1) {
          const block = buf.slice(0, nlIdx);
          buf = buf.slice(nlIdx + 2);
          for (const line of block.split("\n")) {
            if (!line.startsWith("data: ")) continue;
            let evt;
            try { evt = JSON.parse(line.slice(6)); } catch { continue; }
            if (evt.type === "precedents") {
              setMsgs(m => m.map((x, i) => i === assistantIdx ? { ...x, precedents: evt.items || [] } : x));
            } else if (evt.type === "delta") {
              setMsgs(m => m.map((x, i) => i === assistantIdx ? { ...x, content: (x.content || "") + evt.text } : x));
            } else if (evt.type === "done") {
              setMsgs(m => m.map((x, i) => i === assistantIdx ? { ...x, content: evt.full_text || x.content, streaming: false } : x));
            } else if (evt.type === "error") {
              setMsgs(m => m.map((x, i) => i === assistantIdx ? { ...x, content: "Error: " + evt.message, streaming: false } : x));
            }
          }
        }
      }
    } catch (e) {
      setMsgs(m => m.map((x, i) => i === assistantIdx ? { ...x, content: "Error: " + (e?.message || "request failed"), streaming: false } : x));
    } finally {
      setSending(false);
    }
  }, [input, sending, msgs.length, sessionId]);

  const SUGGESTIONS = [
    "What evidence is required for an EW day-labour claim?",
    "When is Pre-Condition Certification required for IRW?",
    "Draft an RFI for missing scale reference in damage photos.",
    "Are catering costs eligible under DRFA Category B?",
  ];

  return (
    <div className="fade-up flex flex-col h-[calc(100vh-8rem)]" data-testid="ai-assistant-page">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div className="label-upper mb-1">Tool</div>
          <h1 className="font-heading text-3xl font-bold">DRFA Expert</h1>
          <p className="text-sm text-slate-500">Grounded in NSW DAG 2021, EPAR Guidelines, EW/IRW Eligibility Criteria, On-Costs Fact Sheet v4, the Standard Treatment Guide v2.1, the Cost Estimation Tool v2.1 — and the RHE assessor precedent corpus.</p>
          <p className="text-[12px] text-blue-700 mt-1.5">
            <ShieldCheck className="w-3 h-3 inline mr-1"/>
            Every answer cites the assessor precedents it drew on. The DRFA Agent is on a trajectory to <b>become the SQP</b> &mdash; today it assists, tomorrow it decides. {" "}
            <Link to="/security" className="underline">Read the trajectory →</Link>
          </p>
        </div>
        <Link to="/precedents" className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.12em] uppercase font-bold rounded-md border border-slate-200 hover:bg-slate-50 shrink-0" data-testid="chat-precedents-link">
          <ShieldCheck className="w-3.5 h-3.5"/> Precedent corpus
        </Link>
      </div>
      <div className="card flex-1 flex flex-col overflow-hidden">
        <div ref={scroller} className="flex-1 overflow-auto p-6 space-y-4">
          {msgs.length === 0 && (
            <div className="text-center text-slate-500 text-sm pt-8">
              <MessageSquareText className="w-8 h-8 mx-auto text-slate-300 mb-2"/>
              Ask anything about DRFA assessments.
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl mx-auto text-left">
                {SUGGESTIONS.map(s => (
                  <button key={s} className="card card-hover p-3 text-sm text-slate-700 text-left" onClick={() => setInput(s)} data-testid={`suggest-${s.slice(0,12)}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={m.role === "user" ? "bubble-user" : "bubble-ai max-w-2xl"}>
                <div className="whitespace-pre-wrap">{m.content || (m.streaming ? "…" : "")}</div>
                {m.role === "assistant" && m.precedents && m.precedents.length > 0 && (
                  <PrecedentCitations precedents={m.precedents} idx={i} />
                )}
              </div>
            </div>
          ))}
          {sending && msgs[msgs.length - 1]?.role !== "assistant" && (
            <div className="flex justify-start"><div className="bubble-ai flex items-center gap-2 text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin"/> Thinking…</div></div>
          )}
        </div>
        <div className="border-t border-slate-200 p-4 flex gap-2">
          <input className="input flex-1" placeholder="Ask the DRFA Expert…" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") send(); }} data-testid="chat-input" />
          <button className="btn-primary flex items-center gap-2" onClick={send} disabled={sending || !input.trim()} data-testid="chat-send-button"><Send className="w-4 h-4"/>Send</button>
        </div>
      </div>
    </div>
  );
}

/* Citation strip for [Precedent N] tags — appears under any assistant reply
 * that drew on the corpus. Each card shows the decision, the line, the
 * assessor, the date, and links back to the source job. */
function PrecedentCitations({ precedents, idx }) {
  return (
    <div className="mt-3 pt-3 border-t border-slate-200" data-testid={`precedent-citations-${idx}`}>
      <div className="text-[10px] tracking-[0.16em] uppercase font-bold text-emerald-700 mb-2 flex items-center gap-1.5">
        <ShieldCheck className="w-3 h-3"/> Drew on {precedents.length} assessor precedent{precedents.length === 1 ? "" : "s"}
      </div>
      <div className="space-y-1.5">
        {precedents.map((p, n) => (
          <div key={p.id} className="rounded-md border border-emerald-100 bg-emerald-50/40 p-2 text-[11.5px] leading-relaxed text-slate-700" data-testid={`precedent-${p.id}`}>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-mono font-bold text-emerald-800">[Precedent {n + 1}]</span>
              <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200 text-[10px] uppercase tracking-wider font-semibold">{p.decision}</span>
              {p.portal_type && <span className="text-[10px] text-slate-500">· {p.portal_type}</span>}
              {p.amount && <span className="text-[10px] text-slate-500 font-mono">· ${Number(p.amount).toLocaleString()}</span>}
            </div>
            {p.line_text && <div className="text-slate-700 mb-0.5">{p.line_text}</div>}
            {p.reason && <div className="text-slate-600 italic">"{p.reason}"</div>}
            <div className="text-[10px] text-slate-500 mt-0.5">
              {p.assessor_email || "anonymous"} · {(p.created_at || "").slice(0, 10)}
              {p.job_top_dir && <> · <span className="font-mono">{p.job_top_dir}</span></>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
